import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Clock, Sparkles, Lock, History, AlertTriangle, ChevronDown } from 'lucide-react';
import api from '../../../utils/api';
import {
  Sheet, Field, Input, Textarea, Select, Switch, Button, Banner, Pill, useLoad, errMsg, fmtDate, todayIso, hoursOf,
} from '../kit';
import { dmy, lockText, payLabel, payTone, EditedLine, NoteHistoryModal } from './timeLock';
import ParticipantPicker from './ParticipantPicker';
import {
  useScMe, readLastActivity, rememberActivity, rememberParticipant, localYmd, localHm,
} from './participants';

// A time entry: one row a coordinator is paid for and a participant is charged for. Start and end
// time, an activity and the note are required; the company rounding applies. Saving is final: there
// is nothing to send on. A locked entry (its claim file or invoice was downloaded or sent, or its pay
// batch was paid) keeps its time; the case note stays editable and time is corrected with an
// adjustment.
//
// The sheet is built for the call you just finished: participant (yours first) → a "30 min" chip →
// the activity you used last is already chosen → the note → Save. Budget, service and coordinator
// sit under "More options" because they almost always keep their defaults.

const CANCEL = [
  { value: 'NSDH', label: 'Health reasons' },
  { value: 'NSDF', label: 'Family issues' },
  { value: 'NSDT', label: 'Transport not available' },
  { value: 'NSDO', label: 'Another reason' },
];
const FLAG_TEXT = {
  overlap: 'Overlaps another of your entries that day',
  daily_hours: 'Your day is over the daily hours limit',
  outside_plan: 'Outside the participant’s plan dates',
  outside_agreement: 'Outside the service agreement dates',
};
export const flagText = (f) => FLAG_TEXT[f?.code] || f?.message || String(f?.code || f || '');

const toMin = (t) => { if (!t || !/^\d{1,2}:\d{2}/.test(t)) return null; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
export const roundMinutes = (m, inc) => {
  if (!m) return 0;
  const i = Math.max(1, Number(inc) || 1);
  return Math.max(i, Math.round(m / i) * i);
};
const DURATIONS = [{ min: 15, label: '15 min' }, { min: 30, label: '30 min' }, { min: 45, label: '45 min' }, { min: 60, label: '1 h' }];
const EMPTY = [];
// Now, to the nearest minute, on the browser's clock.
const nowToMinute = () => new Date(Math.round(Date.now() / 60000) * 60000);

export default function TimeEntryForm({
  open, onClose, coordinationId, entry, budgets: budgetsProp, services: servicesProp, participantName, staff = EMPTY, canLogForOthers, onSaved, defaults,
}) {
  const me = useScMe({ skip: !open });
  const types = useLoad('/sc/activity-types', null, { skip: !open });
  const rounding = me.settings.rounding_minutes || 1;
  const claiming = me.perms.includes('claiming');
  const locked = !!entry?.locked;
  const [f, setF] = useState({});
  const [picked, setPicked] = useState(null);       // { id, name } when the sheet asked for the participant
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [flags, setFlags] = useState([]);
  const [history, setHistory] = useState(null);
  const noteId = useId();
  const moreId = useId();

  const askParticipant = !coordinationId && !entry;
  const coordId = coordinationId || (picked && picked.id) || null;
  const who = participantName || (picked && picked.name) || '';

  // Budgets and services: a participant's file passes its own; anywhere else they are loaded once the
  // participant is known.
  const needEng = open && !locked && !!coordId && (budgetsProp === undefined || servicesProp === undefined);
  const eng = useLoad(needEng ? `/support-coordination/${coordId}` : null);
  const budgets = budgetsProp || (needEng && eng.data && String(eng.data.id) === String(coordId) ? eng.data.budgets : null) || EMPTY;
  const services = servicesProp || (needEng && eng.data && String(eng.data.id) === String(coordId) ? eng.data.services : null) || EMPTY;

  // Reset the form when it opens or switches entry — not when the parent re-renders with a new
  // defaults/budgets array, which would wipe what the person has typed.
  const seed = useRef({ defaults, budgets });
  seed.current = { defaults, budgets };
  const activitySeeded = useRef(false);
  const budgetSeededFor = useRef(null);
  useEffect(() => {
    if (!open) return;
    const { defaults: d, budgets: bs } = seed.current;
    setFlags(entry?.flags || []);
    setPicked(null);
    setMore(false);
    activitySeeded.current = !!(entry || d?.activity_type_id);
    budgetSeededFor.current = entry || (bs && bs.length) ? 'seeded' : null;
    setF({
      note_date: (entry?.note_date || d?.note_date || todayIso()).slice(0, 10),
      start_time: (entry?.start_time || d?.start_time || '').slice(0, 5),
      end_time: (entry?.end_time || d?.end_time || '').slice(0, 5),
      activity_type_id: entry?.activity_type_id || d?.activity_type_id || '',
      billable: entry ? entry.billable !== false : undefined,
      cancellation_reason: entry?.cancellation_reason || '',
      budget_id: entry?.budget_id || ((bs || []).find((b) => b.is_active) || {}).id || '',
      service_id: entry?.service_id || '',
      coordinator_id: entry?.coordinator_id || '',
      summary: entry?.summary || d?.summary || '',
    });
  }, [open, entry]);

  // The activity you used last is chosen for you (a new entry only, and only if it still exists).
  useEffect(() => {
    if (!open || activitySeeded.current || !types.data) return;
    activitySeeded.current = true;
    const last = readLastActivity();
    if (last && types.data.some((t) => String(t.id) === last)) setF((p) => (p.activity_type_id ? p : { ...p, activity_type_id: last }));
  }, [open, types.data]);

  // A participant chosen here gets their first active budget, the same default a file gives.
  useEffect(() => {
    if (!coordId || budgetSeededFor.current === 'seeded' || budgetSeededFor.current === coordId || !budgets.length) return;
    budgetSeededFor.current = coordId;
    setF((p) => ({ ...p, budget_id: (budgets.find((b) => b.is_active) || {}).id || '' }));
  }, [coordId, budgets]);

  const type = useMemo(() => (types.data || []).find((t) => t.id === f.activity_type_id), [types.data, f.activity_type_id]);
  const needsReason = type?.claim_type === 'CANC';
  // The activity sets billable unless the person changes it.
  const billable = f.billable === undefined ? (type ? type.billable_default !== false : true) : f.billable;
  const raw = (() => { const a = toMin(f.start_time); const b = toMin(f.end_time); return a != null && b != null && b > a ? b - a : null; })();
  const rounded = raw == null ? null : roundMinutes(raw, rounding);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v && v.target ? v.target.value : v }));

  // A cancellation needs its reason, which lives under More options: open it so it can't be missed.
  useEffect(() => { if (needsReason) setMore(true); }, [needsReason]);

  const choose = (p) => {
    setPicked(p ? { id: p.id, name: p.name } : null);
    budgetSeededFor.current = null;
    setF((x) => ({ ...x, budget_id: '', service_id: '' }));
  };

  // Quick times: the call just ended now and took this long. "Start now" starts the clock instead.
  const quick = (mins) => {
    const end = nowToMinute();
    const start = new Date(end.getTime() - mins * 60000);
    setF((p) => ({ ...p, note_date: localYmd(end), end_time: localHm(end), start_time: localYmd(start) === localYmd(end) ? localHm(start) : '00:00' }));
  };
  const startNow = () => { const n = nowToMinute(); setF((p) => ({ ...p, note_date: localYmd(n), start_time: localHm(n), end_time: '' })); };

  const aiDraft = async () => {
    if (!String(f.summary || '').trim()) return toast.error('Type a few points in the note first, then draft with AI.');
    setAiBusy(true);
    try {
      const r = await api.post('/support-coordination/ai/draft-note', { bullets: f.summary, contact_type: type?.name, participant_name: who || undefined });
      if (r.data?.text) setF((p) => ({ ...p, summary: r.data.text }));
      toast.success('Drafted. Check it before saving.');
    } catch (e) { toast.error(errMsg(e, 'The AI draft didn’t work. Your points are still in the note.')); } finally { setAiBusy(false); }
  };

  const save = async (e) => {
    e?.preventDefault();
    if (!coordId) return toast.error('Choose who this time is for.');
    if (!f.start_time || !f.end_time) return toast.error(f.start_time ? 'Enter the end time, or tap a quick time.' : 'Enter the start and end time, or tap a quick time.');
    if (raw == null) return toast.error('The end time must be after the start time.');
    if (!f.activity_type_id) return toast.error('Choose the activity.');
    if (!String(f.summary || '').trim()) return toast.error('Write the note: what happened, what was agreed, what’s next.');
    if (needsReason && !f.cancellation_reason) { setMore(true); return toast.error('Choose why it was cancelled, under More options.'); }
    setBusy(true);
    const body = {
      coordination_id: coordId, note_date: f.note_date, start_time: f.start_time, end_time: f.end_time,
      activity_type_id: f.activity_type_id, billable, summary: f.summary,
      cancellation_reason: needsReason ? f.cancellation_reason : undefined,
      budget_id: f.budget_id || null, service_id: f.service_id || null,
      coordinator_id: canLogForOthers && f.coordinator_id ? f.coordinator_id : undefined,
    };
    try {
      const r = entry ? await api.put(`/sc/time-entries/${entry.id}`, body) : await api.post('/sc/time-entries', body);
      const fl = r.data?.flags || [];
      setFlags(fl);
      if (!entry) { rememberParticipant(me.userId, coordId); rememberActivity(f.activity_type_id); }
      const h = hoursOf(r.data?.entry?.minutes_rounded ?? rounded);
      toast.success(entry ? 'Time entry updated' : `Saved ${h} h${who ? ` for ${who}` : ''}`);
      if (fl.length) toast(fl.map(flagText).join(' · '), { icon: '⚑', duration: 6000 });
      onSaved?.(r.data?.entry);
    } catch (err) { toast.error(errMsg(err, 'Could not save the time entry. Please try again.')); } finally { setBusy(false); }
  };

  const typeOpts = (types.data || []).map((t) => ({ value: t.id, label: t.name }));
  const activityHint = type ? (
    <span>
      <span className="text-slate-600">{billable ? 'Billable · charged to the plan' : 'Not billable'}</span>
      {claiming && type.claim_type ? <span className="text-slate-500"> · claim type {type.claim_type}</span> : null}
    </span>
  ) : types.error ? <span className="text-rose-600">Couldn’t load the activities. Close and open Log time again.</span> : undefined;

  const activeBudgets = budgets.filter((b) => b.is_active !== false);
  const activeServices = services.filter((s) => s.is_active !== false);
  const showBudget = billable && activeBudgets.length > 0;
  const showService = billable && activeServices.length > 0;
  const showCoordinator = !!canLogForOthers && staff.length > 0;
  const hasMore = showBudget || showService || showCoordinator || needsReason;
  const budgetName = (activeBudgets.find((b) => String(b.id) === String(f.budget_id)) || {});
  const serviceName = (activeServices.find((s) => String(s.id) === String(f.service_id)) || {});
  const coordName = (staff.find((s) => String(s.id) === String(f.coordinator_id)) || {});
  const moreSummary = [
    needsReason && (f.cancellation_reason ? `Cancelled: ${(CANCEL.find((c) => c.value === f.cancellation_reason) || {}).label}` : 'Cancellation reason needed'),
    showBudget && `Budget: ${budgetName.budget_name || budgetName.name || 'not set'}`,
    showService && `Service: ${serviceName.service_name || serviceName.support_item_code || 'default'}`,
    showCoordinator && `Coordinator: ${coordName.first_name ? `${coordName.first_name} ${coordName.last_name || ''}`.trim() : 'me'}`,
  ].filter(Boolean).join(' · ');

  return (
    <Sheet open={open} onClose={onClose} icon={Clock} width="md"
      title={entry ? (locked ? 'Time entry' : 'Edit time entry') : 'Log time'}
      sub={who ? `${who}${entry ? ` · ${fmtDate(entry.note_date)}` : ''}` : undefined}
      footer={locked ? <Button variant="secondary" onClick={onClose}>Close</Button> : (
        <><Button variant="secondary" onClick={onClose}>Cancel</Button><Button type="submit" form="sc-time-form" loading={busy}>{entry ? 'Save changes' : 'Save time'}</Button></>
      )}>
      {locked ? <LockedEntry entry={entry} participantName={participantName} onAdjusted={onSaved} rounding={rounding} /> : (
        <form id="sc-time-form" onSubmit={save} className="space-y-5">
          {askParticipant && (
            <Field label="Participant" required>
              <ParticipantPicker value={picked} onChange={choose} me={me} today={todayIso()} autoFocus />
            </Field>
          )}

          <div className="space-y-2.5">
            <div className="grid grid-cols-2 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3">
              <Field label="Date" required className="col-span-2 sm:col-span-1">
                <Input type="date" value={f.note_date || ''} max={todayIso()} onChange={set('note_date')} className="min-w-0" />
              </Field>
              <Field label="Start" required><Input type="time" value={f.start_time || ''} onChange={set('start_time')} className="min-w-0" /></Field>
              <Field label="End" required><Input type="time" value={f.end_time || ''} onChange={set('end_time')} className="min-w-0" /></Field>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <div role="group" aria-label="Quick times, ending now" className="flex flex-wrap gap-1.5">
                {DURATIONS.map((d) => (
                  <Chip key={d.min} pressed={raw === d.min} onClick={() => quick(d.min)} title={`Ended now, took ${d.label}`}>{d.label}</Chip>
                ))}
                <Chip onClick={startNow} title="Start time now; add the end time when you finish">Start now</Chip>
              </div>
              <p className="basis-full sm:basis-auto sm:ml-auto text-[12.5px] text-slate-600 tabular-nums" aria-live="polite">
                {rounded == null ? 'No time yet' : <><span className="font-semibold text-violet-800">{hoursOf(rounded)} h</span> recorded</>}
                {raw != null && raw !== rounded && <span className="text-slate-500"> · {raw} min worked, rounded to {rounded}</span>}
              </p>
            </div>
          </div>

          <Field label="Activity" required hint={activityHint}>
            <Select value={f.activity_type_id || ''} onChange={(v) => setF((p) => ({ ...p, activity_type_id: v, billable: undefined }))}
              options={typeOpts} placeholder={types.loading && !types.data ? 'Loading activities…' : 'What was this time for?'} />
          </Field>

          <div className="space-y-1.5">
            <label htmlFor={noteId} className="block text-[12.5px] font-semibold text-slate-700">Note<span className="text-rose-500 ml-0.5" aria-hidden>*</span></label>
            <Textarea id={noteId} rows={5} aria-required="true" value={f.summary || ''} onChange={set('summary')} placeholder="What happened, what was agreed, what’s next." />
            <div className="flex flex-wrap items-center justify-between gap-2">
              {entry ? <EditedLine entry={entry} onHistory={setHistory} /> : <span />}
              <Button size="sm" variant="soft" icon={Sparkles} loading={aiBusy} onClick={aiDraft}>Draft with AI</Button>
            </div>
          </div>

          {type && (
            <Switch label="Billable" hint={billable ? undefined : 'Recorded (and paid if the activity is paid), never charged to the plan.'}
              checked={billable} onChange={(v) => setF((p) => ({ ...p, billable: v }))} />
          )}

          {hasMore && (
            <div className="rounded-xl ring-1 ring-inset ring-slate-200">
              <button type="button" aria-expanded={more} aria-controls={moreId} onClick={() => setMore((v) => !v)}
                className="w-full min-h-[44px] flex items-center justify-between gap-3 px-4 py-2.5 text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-slate-800">More options</span>
                  {!more && moreSummary && <span className={clsx('block text-[12px] truncate', needsReason && !f.cancellation_reason ? 'text-amber-800 font-semibold' : 'text-slate-500')}>{moreSummary}</span>}
                </span>
                <ChevronDown size={16} className={clsx('text-slate-500 flex-shrink-0 transition-transform', more && 'rotate-180')} aria-hidden />
              </button>
              {more && (
                <div id={moreId} className="px-4 pb-4 pt-1 space-y-4">
                  {needsReason && (
                    <Field label="Why was it cancelled?" required>
                      <Select value={f.cancellation_reason || ''} onChange={set('cancellation_reason')}
                        options={CANCEL.map((c) => ({ ...c, hint: claiming ? c.value : undefined }))} placeholder="Choose the reason" />
                    </Field>
                  )}
                  {(showBudget || showService) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {showBudget && <Field label="Budget"><Select value={f.budget_id || ''} onChange={set('budget_id')} allowEmpty emptyLabel="Not set"
                        options={activeBudgets.map((b) => ({ value: b.id, label: b.budget_name || b.name || 'Budget' }))} /></Field>}
                      {showService && <Field label="Service" hint="Default: the participant’s support coordination service."><Select value={f.service_id || ''} onChange={set('service_id')} allowEmpty emptyLabel="Default"
                        options={activeServices.map((s) => ({ value: s.id, label: s.service_name || s.support_item_code }))} /></Field>}
                    </div>
                  )}
                  {showCoordinator && (
                    <Field label="Support coordinator" hint="Leave as yourself unless you are entering time for someone else.">
                      <Select value={f.coordinator_id || ''} onChange={set('coordinator_id')} allowEmpty emptyLabel="Me" options={staff.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name}` }))} />
                    </Field>
                  )}
                </div>
              )}
            </div>
          )}
          {needEng && eng.error && <p className="text-[12px] text-slate-600">Couldn’t load this participant’s budgets, so the default budget is used.</p>}

          {flags.length > 0 && (
            <Banner tone="amber" title="Check this entry">
              <ul className="list-disc pl-4 space-y-0.5">{flags.map((x, i) => <li key={i}>{flagText(x)}</li>)}</ul>
            </Banner>
          )}
        </form>
      )}
      {!locked && <NoteHistoryModal entry={history} onClose={() => setHistory(null)} />}
    </Sheet>
  );
}

function Chip({ pressed, onClick, title, children }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-pressed={pressed === undefined ? undefined : !!pressed}
      className={clsx('h-11 sm:h-8 px-2.5 sm:px-3 rounded-full text-[12.5px] font-semibold whitespace-nowrap ring-1 ring-inset transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        pressed ? 'bg-violet-600 text-white ring-violet-600' : 'bg-white text-slate-700 ring-slate-200 hover:bg-violet-50 hover:text-violet-800 hover:ring-violet-200')}>
      {children}
    </button>
  );
}

// A locked entry (its claim file or invoice was downloaded or sent, or its pay batch was paid): the
// time and the fields that make up the claim line are read only, with the lock reason on hover. The
// case note stays editable with its own Save note (every edit is a new version), and time is
// corrected with Add adjustment (+/- minutes and a reason, saved straight away). No Delete.
function LockedEntry({ entry, participantName, onAdjusted, rounding }) {
  const detail = useLoad(`/sc/time-entries/${entry.id}`);
  const [mins, setMins] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(entry.summary || '');
  const [noteBusy, setNoteBusy] = useState(false);
  const [history, setHistory] = useState(null);
  const noteId = useId();
  const e = detail.data || entry;
  const loadedSummary = detail.data ? detail.data.summary : null;
  // The note box follows the saved text once the full entry has loaded.
  useEffect(() => { if (loadedSummary != null) setNote(loadedSummary || ''); }, [loadedSummary]);
  const why = lockText(e);
  const blank = !String(note || '').trim();
  const noteChanged = String(note || '') !== String(e.summary || '');

  const saveNote = async () => {
    if (blank) return toast.error('The case note can’t be blank.');
    setNoteBusy(true);
    try {
      const r = await api.put(`/sc/time-entries/${entry.id}/note`, { summary: note });
      toast.success('Case note saved. The time, claim and pay are unchanged.');
      detail.reload();
      onAdjusted?.(r.data?.entry);
    } catch (err) { toast.error(errMsg(err, 'Could not save the case note. Please try again.')); } finally { setNoteBusy(false); }
  };

  const adjust = async () => {
    const n = parseInt(mins, 10);
    if (!n) return toast.error('Enter the minutes to add (for example 15) or remove (for example -15).');
    if (!reason.trim()) return toast.error('Give the reason for the adjustment.');
    setBusy(true);
    try {
      await api.post(`/sc/time-entries/${entry.id}/adjust`, { minutes: n, reason: reason.trim() });
      toast.success('Adjustment saved. It goes into the next claim batch and the next open pay period.');
      setMins(''); setReason(''); detail.reload(); onAdjusted?.();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const who = participantName || `${e.client_first || ''} ${e.client_last || ''}`.trim() || '—';
  const coord = `${e.coordinator_first || ''} ${e.coordinator_last || ''}`.trim() || '—';
  return (
    <div className="space-y-5">
      <Banner tone="violet" icon={Lock} title="The time on this entry is locked">{why} The case note can still be edited.</Banner>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <LockedField label="Date" value={dmy(e.note_date)} why={why} className="col-span-2 sm:col-span-1" />
        <LockedField label="Start" value={e.start_time || '—'} why={why} />
        <LockedField label="End" value={e.end_time || '—'} why={why} />
        <LockedField label="Duration" value={`${hoursOf(e.minutes_rounded)} h`} why={why} className="col-span-2 sm:col-span-1" />
        <LockedField label="Participant" value={who} why={why} className="col-span-2" />
        <LockedField label="Support coordinator" value={coord} why={why} className="col-span-2" />
        <LockedField label="Activity type" value={e.activity_name || '—'} why={why} className="col-span-2" />
        <LockedField label="Billable" value={e.billable ? 'Billable' : 'Non-billable'} why={why} className="col-span-2" />
      </div>
      <p className="flex flex-wrap items-center gap-2 text-[12.5px] text-slate-600">
        <span>Pay</span><Pill size="xs" tone={payTone(e.pay_status)} label={payLabel(e.pay_status, e.pay_status_label)} />
      </p>

      <div className="space-y-1.5">
        <label htmlFor={noteId} className="block text-[12.5px] font-semibold text-slate-700">Case note<span className="text-rose-500 ml-0.5" aria-hidden>*</span></label>
        <Textarea id={noteId} rows={6} aria-required="true" aria-invalid={blank ? true : undefined} value={note} onChange={(ev) => setNote(ev.target.value)}
          placeholder="What happened, what was agreed, what’s next." />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <EditedLine entry={e} onHistory={setHistory} />
          <Button size="sm" className="ml-auto" loading={noteBusy} disabled={!noteChanged || blank} onClick={saveNote}>Save note</Button>
        </div>
        {blank && <p className="text-[12px] text-rose-600">The case note can’t be blank.</p>}
      </div>

      {(e.adjustments || []).length > 0 && (
        <div>
          <p className="text-[12.5px] font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><History size={14} aria-hidden /> Adjustments</p>
          <ul className="space-y-1.5">
            {e.adjustments.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 text-[13px] rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-slate-600 truncate">{a.adjustment_reason}</span>
                <span className={`tabular-nums font-semibold ${a.minutes_rounded < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{a.minutes_rounded > 0 ? '+' : ''}{a.minutes_rounded} min</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="rounded-2xl ring-1 ring-slate-200 p-4 space-y-3">
        <p className="text-[13px] font-semibold text-slate-900">Add adjustment</p>
        <p className="text-[12.5px] text-slate-500">Add or remove minutes with a reason. It is a new entry linked to this one and saves straight away.</p>
        <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3">
          <Field label="Minutes" hint={rounding > 1 ? `Rounded to ${rounding}` : undefined}><Input type="number" step="1" value={mins} onChange={(ev) => setMins(ev.target.value)} placeholder="15 or -15" /></Field>
          <Field label="Reason" required><Input value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder="The meeting ran 15 minutes longer" /></Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[12px] text-slate-500 flex items-center gap-1"><AlertTriangle size={12} aria-hidden /> Claim: the next claim batch. Pay: the next open pay period.</p>
          <Button size="sm" loading={busy} onClick={adjust}>Save adjustment</Button>
        </div>
      </div>
      <NoteHistoryModal entry={history} onClose={() => setHistory(null)} />
    </div>
  );
}

// A read-only, greyed-out field on a locked entry; hovering it shows why it can't change.
function LockedField({ label, value, why, className }) {
  const id = useId();
  return (
    <div className={clsx('space-y-1.5 min-w-0', className)} title={why}>
      <label htmlFor={id} className="flex items-center gap-1 text-[12.5px] font-semibold text-slate-500">
        <Lock size={11} aria-hidden />{label}
      </label>
      <input id={id} readOnly aria-readonly="true" value={value} title={why}
        className="w-full h-11 sm:h-10 rounded-xl border-0 bg-slate-100 ring-1 ring-inset ring-slate-200 px-3 text-[13.5px] text-slate-500 cursor-not-allowed truncate focus:outline-none focus:ring-2 focus:ring-indigo-500" />
    </div>
  );
}
